import { computed, ref } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'
import { getTimezoneOffsetInHours } from 'www/utils'
import { DAYS_FOR_DOCTOR_SLOTS } from 'www/modules/Schedule/constants'
import { apiScheduleDoctors, apiScheduleServices, apiScheduleTelemed } from 'www/api'
import type { Slots } from 'www/api/types'
import { type RedirectValue } from 'www/constants'
import { useDoctorPageLpuListStore, useExternalDataStore } from 'www/vue_components/common/DoctorPageLpuList/stores'
import { ScheduleType } from 'www/api/apiScheduleTelemed.types'
import { LpuAddressList, SelectWorkplaceOrServiceItem, SynonymPriceList } from 'www/vue_components/common/DoctorPageLpuList/types'
import { getPriceList } from 'www/vue_components/common/DoctorPageLpuList/functions'

export default (lpuId: number) => defineStore(`doctorAppointmentCard/${lpuId}`, () => {
  const doctorPageLpuListStore = useDoctorPageLpuListStore()
  const { filterType } = storeToRefs(doctorPageLpuListStore)
  const externalDataStore = useExternalDataStore()
  const { currentDate, townTimedelta } = storeToRefs(externalDataStore)

  const lpuAddress = ref<LpuAddressList[number]>()
  const lpuId = ref(0)
  const doctorId = ref(0)
  const timedelta = ref(getTimezoneOffsetInHours())
  const hasSlots = ref(false)
  const workplaces = ref<LpuAddressList[number]['workplaces']>([])
  const services = ref<SynonymPriceList>([])
  const nowDateString = ref(format(new Date(), 'yyyy-MM-dd', { locale: ru }))
  const selectedItem = ref<SelectWorkplaceOrServiceItem>({
    price: null,
    agesText: '',
    isWorkplace: false,
  })
  const calendarSlots = ref<Slots>({})
  const isRequestSlotsDone = ref(false)
  const redirectValue = ref<RedirectValue>()

  const selectedItemPriceList = computed(() => getPriceList(selectedItem.value.workplace))

  const scheduleType = computed<ScheduleType>(() => {
    const { priceOnline, priceMedtochkaTelemed } = selectedItemPriceList.value

    return apiScheduleTelemed.getScheduleTypeV2({
      telemed: priceOnline,
      telemedMedtochka: priceMedtochkaTelemed,
    })
  })

  const workplacesWithSelectedFilterType = computed(() => workplaces.value.filter(workplace => {
    const priceList = getPriceList(workplace)

    switch (filterType.value) {
      case 'clinic':
        return typeof priceList.price === 'number'
      case 'home':
        return typeof priceList.priceHome === 'number'
      case 'telemed':
        return typeof priceList.priceOnline === 'number' || typeof priceList.priceMedtochkaTelemed === 'number'
    }

    return false
  }))

  function resetSlots() {
    calendarSlots.value = {}
  }

  async function requestCalendarSlots() {
    const days = DAYS_FOR_DOCTOR_SLOTS

    try {
      let slots: Slots | null = null

      isRequestSlotsDone.value = false

      if (filterType.value === 'telemed') {
        const response = await apiScheduleTelemed.post({
          days,
          user_start_date: currentDate.value,
          only_free: true,
          user_timezone: getTimezoneOffsetInHours(),
          lpu_params: [{
            lpu_id: lpuId.value,
            schedule_params: [{
              doctor_id: doctorId.value ?? 0,
              type_schedule: scheduleType.value,
            }],
          }],
        })

        slots = apiScheduleTelemed.transformSlots(response.data[0].schedule_data[0]?.schedule)
      } else if (filterType.value === 'clinic') {
        if (selectedItem.value.isWorkplace) {
          const response = await apiScheduleDoctors.post({
            days,
            dt_start: currentDate.value,
            all_slots: false,
            town_timedelta: townTimedelta.value ?? 0,
            doctors_lpus: [{
              lpu_id: lpuId.value,
              doctor_id: doctorId.value,
              lpu_timedelta: timedelta.value,
              has_slots: hasSlots.value,
            }],
          })

          slots = response.data.result[0]?.slots
        } else {
          const response = await apiScheduleServices.post({
            days,
            day_start: currentDate.value,
            only_free: true,
            town_timedelta: townTimedelta.value ?? 0,
            services: [{
              lpu_id: lpuId.value,
              service_id: selectedItem.value.service?.synonym_id ?? 0,
              doctor_id: doctorId.value ?? 0,
            }],
          })

          slots = response.data.result[0]?.slots
        }
      }

      if (slots) {
        calendarSlots.value = slots
      }
    } catch { /* Empty */ } finally {
      isRequestSlotsDone.value = true
    }
  }

  return {
    lpuAddress,
    lpuId,
    doctorId,
    timedelta,
    hasSlots,
    workplaces,
    workplacesWithSelectedFilterType,
    services,
    nowDateString,
    selectedItem,
    selectedItemPriceList,
    scheduleType,
    calendarSlots,
    isRequestSlotsDone,
    redirectValue,
    resetSlots,
    requestCalendarSlots,
  }
})
